import type { TenantBilling } from "../schemas/tenant.js";
import { dollarsToMicros } from "../spend/index.js";
import { METERED_COUNTERS, type MeteredCounts, OVERAGE_UNIT_MICROS, type OverageUnits, PLANS, type Plan, overageUnits } from "./plans.js";

export type PaidBilling = Extract<TenantBilling, { state: "paid" }>;

/** A period's counters and the overage that housekeeping already sent to Stripe. The `PERIOD#` item has these fields. */
export interface PeriodTotals extends MeteredCounts {
  reportedOverageUnits: number;
}

/** The overage that the period's usage owes under a plan: the whole period against the plan's allowance. */
export function periodOverageUnits(plan: Plan, usage: MeteredCounts): OverageUnits {
  if (plan.overageUnits === null) return overageUnits(0);
  let total = 0;
  for (const counter of METERED_COUNTERS) total += Math.max(0, usage[counter] - plan.included[counter]) * plan.overageUnits[counter];
  return overageUnits(total);
}

/**
 * The plan price plus overage, in millionths of a dollar. Free is always 0.
 * Overage that was already reported is never taken back, so it counts when it
 * is more than the plan now owes (for example after a change to a bigger plan).
 */
export function estimatedBillMicros(plan: Plan, totals: PeriodTotals): number {
  if (plan.overageUnits === null) return 0;
  const owed = Math.max(totals.reportedOverageUnits, periodOverageUnits(plan, totals));
  return dollarsToMicros(plan.priceUsd) + owed * OVERAGE_UNIT_MICROS;
}

/** `1,234` */
export function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

/** `$1,197` */
export function formatUsd(usd: number): string {
  return `$${formatCount(usd)}`;
}

/** `$0.015` or `$31.50`: a unit price as it is, a total to the cent. */
export function formatMicrosUsd(micros: number, precision: "unit" | "cents"): string {
  const usd = micros / 1_000_000;
  return precision === "unit" ? `$${String(usd)}` : `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const BILLING_HOLD_KINDS = ["free_limit", "bill_limit"] as const;
export type BillingHoldKind = (typeof BILLING_HOLD_KINDS)[number];

export interface BillingHold {
  kind: BillingHoldKind;
  /** The held reason and the trace text. */
  reason: string;
}

/** Why one more message must be held, or undefined when it may go to the router. */
export function messageHold(billing: TenantBilling, totals: PeriodTotals): BillingHold | undefined {
  if (billing.state !== "paid") {
    const allowance = PLANS.free.included.messages;
    return totals.messages >= allowance ? { kind: "free_limit", reason: `Monthly free limit reached (${allowance})` } : undefined;
  }
  return billLimitHold(billing, { ...totals, messages: totals.messages + 1 });
}

/** Why one more written reply must be held. Free needs no check: it has no more written replies than messages. */
export function writtenReplyHold(billing: TenantBilling, totals: PeriodTotals): BillingHold | undefined {
  if (billing.state !== "paid") return undefined;
  return billLimitHold(billing, { ...totals, writerCalls: totals.writerCalls + 1 });
}

function billLimitHold(billing: PaidBilling, next: PeriodTotals): BillingHold | undefined {
  if (estimatedBillMicros(PLANS[billing.plan], next) <= dollarsToMicros(billing.billLimitUsd)) return undefined;
  return { kind: "bill_limit", reason: `Monthly bill limit reached (${formatUsd(billing.billLimitUsd)})` };
}

export const BILLING_NOTICES = ["allowance80", "allowance100", "billLimit"] as const;
export type BillingNotice = (typeof BILLING_NOTICES)[number];

/**
 * The notices that the usage has reached, most severe allowance notice first.
 * The bill limit counts as reached when the next message or written reply
 * would be held: the hold keeps the bill itself just under the limit.
 */
export function reachedNotices(billing: TenantBilling, totals: PeriodTotals): BillingNotice[] {
  const plan = PLANS[billing.plan];
  const reached = (percent: number): boolean => METERED_COUNTERS.some((counter) => totals[counter] * 100 >= plan.included[counter] * percent);
  const notices: BillingNotice[] = [];
  if (reached(100)) notices.push("allowance100");
  if (reached(80)) notices.push("allowance80");
  if ((messageHold(billing, totals) ?? writtenReplyHold(billing, totals))?.kind === "bill_limit") notices.push("billLimit");
  return notices;
}
